import DoctorFeeOrderPreviewList from "issara-sdk/apis/DoctorFeeOrderPreviewList_apps_DFC";
import DoctorFeeOrderCreateUpdateList from "issara-sdk/apis/DoctorFeeOrderCreateUpdateList_apps_DFC";
import DoctorFeeOrderList from "issara-sdk/apis/DoctorFeeOrderList_apps_DFC";
import DoctorFeeOrderDetail from "issara-sdk/apis/DoctorFeeOrderDetail_apps_DFC";

import MiscellaneousList from "issara-sdk/apis/MiscellaneousList_core";
import MiscellaneousPreviewList from "issara-sdk/apis/MiscellaneousPreviewList_core";
import MiscellaneousOrderListCreate from "issara-sdk/apis/MiscellaneousOrderListCreate_core";
import MiscellaneousOrderList from "issara-sdk/apis/MiscellaneousOrderList_core";
import MiscellaneousOrderDetail from "issara-sdk/apis/MiscellaneousOrderDetail_coreM";

import ProductList from "issara-sdk/apis/ProductList_core";
import ProductServiceListCreateAPIView from "issara-sdk/apis/ProductServiceListCreateAPIView_core";
import ProductServiceRetrieveUpdateAPIView from "issara-sdk/apis/ProductServiceRetrieveUpdateAPIView_core";

import OfficialAccountUserList from "issara-sdk/apis/OfficialAccountUserList_apps_PRX";
import EncounterList from "issara-sdk/apis/EncounterList_core";
import ReportStationSummaryView from "issara-sdk/apis/ReportStationSummaryView_apps_BIL";

import MiscellaneousSerializer from "issara-sdk/types/MiscellaneousSerializer_core";
import ProductSerializer from "issara-sdk/types/ProductSerializer_core";
import ProfiledDivisionHasUserSerializer from "issara-sdk/types/ProfiledDivisionHasUserSerializer_apps_PRX";
import EncounterSerializer from "issara-sdk/types/EncounterSerializer_core";
import ProductServiceSerializer from "issara-sdk/types/ProductServiceSerializer_core";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  BLC_STATUS_TYPE,
  GenerateActionTypes,
  generatePrefixedActions,
  PickMainState,
} from "react-lib/apps/HISV3/common/CommonInterface";

import { MeBillingSequence } from "react-lib/apps/MEH/types/MeBillingType";
import WasmController from "react-lib/frameworks/WasmController";
import { parseDate } from "react-lib/utils/dateUtils";

/** ============== Type-Defining Constants =============  */

export const ACTIONS = {
  SEARCH: "SEARCH",
  ADD_SERVICE_ORDER: "ADD_SERVICE_ORDER",
  SAVE_SERVICE_ORDER: "SAVE_SERVICE_ORDER",
  DELETE_SERVICE_ORDER: "DELETE_SERVICE_ORDER",
  DELETE_SERVICE_DOCTOR: "DELETE_SERVICE_DOCTOR",
  GET_MISCELLANEOUS_LIST: "GET_MISCELLANEOUS_LIST",
  SELECTED_ENCOUNTER: "SELECTED_ENCOUNTER",
  SEARCH_PRODUCT: "SEARCH_PRODUCT",
  CREATE_SERVICE: "CREATE_SERVICE",
  SEARCH_SERVICE: "SEARCH_SERVICE",
} as const;

export const SEQUENCE_NAME = "SaveServiceOrder" as const;
export const MAIN_CARD_NAME = "CardSaveServiceOrder" as const;

export const DEFAULT_PRODUCT_LIST_LIMIT = 10 as const;

/** =============== Types and Interfaces ===============  */
export type State = Partial<{
  selectedEncounter?: any;
  providerEmployeeInfo?: any;
  SaveServiceOrderSequence: SaveServiceOrderType;
  MeBillingSequence?: MeBillingSequence;
}>;

export type SaveServiceOrderType = Partial<{
  sequenceIndex: "Action" | "START" | null;
  serviceOrderPreviewData: Record<string, any>[];
  serviceDoctorPreviewData: Record<string, any>[];
  oaUserList?: ProfiledDivisionHasUserSerializer[];
  encounterList?: EncounterSerializer[];
  miscellaneousList?: MiscellaneousSerializer[];
  productList?: ProductSerializer[];
  productListPage?: {
    activePage?: number | null;
    total?: number | null;
  };
  serviceList?: ProductServiceSerializer[];
  serviceListPage?: {
    activePage?: number | null;
    total?: number | null;
  };
}> | null;

export type PickedState = PickMainState<
  | "buttonLoadCheck"
  | "errorMessage"
  | "successMessage"
  | "loadingStatus"
  | "selectedDivision"
  | "userTokenize"
  | "masterOptions"
>;

export type Event = { message: "GetMasterData"; params: Record<string, any> };

export type Data = { division?: number; device?: number };

export type SeqState = BaseSeqState<SequenceName>;

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.SEARCH]: { searchText?: string; activePage?: number; filter?: MiscellaneousListParams };
  [ACTIONS.ADD_SERVICE_ORDER]: {
    drafId?: string;
    selectService?: MiscellaneousSerializer[];
    callback?: (open: boolean) => void;
  };
  [ACTIONS.SAVE_SERVICE_ORDER]: {};
  [ACTIONS.DELETE_SERVICE_ORDER]: { id: number | null; callback?: (open: number | null) => void };
  [ACTIONS.DELETE_SERVICE_DOCTOR]: { data: any };
  [ACTIONS.GET_MISCELLANEOUS_LIST]: {};
  [ACTIONS.SELECTED_ENCOUNTER]: { encounterData?: EncounterSerializer | Record<string, any> };
  [ACTIONS.SEARCH_PRODUCT]: {
    searchText?: string;
    activePage?: number;
    filter?: ProductListParams;
  };
  [ACTIONS.CREATE_SERVICE]: {
    data: Partial<ProductServiceSerializer>;
    isEdit: boolean;
    callback?: (open: boolean) => void;
  };
  [ACTIONS.SEARCH_SERVICE]: {
    activePage?: number;
    filter?: ProductServiceParams;
  };
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

export type Controller = WasmController<PickedState & State, Event, Data, {}>;

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

type LoadingStatusKey = keyof PickedState["loadingStatus"];

type ButtonLoadCheckKey = keyof PickedState["buttonLoadCheck"];

type ErrorMessageKey = keyof PickedState["errorMessage"];

type SuccessMessageKey = keyof PickedState["successMessage"];

/** =================== Params Types ===================  */

export type MiscellaneousListParams = Partial<{
  code: string;
  name: string;
  service_id: number;
  keyword: string;
  limit: number;
  offset: number;
  encounter: number;
}>;

export type ProductListParams = Partial<{
  code: string;
  name: string;
  active_flag: number;
  search: string;
  limit: number;
  offset: number;
}>;

export type ProductServiceParams = Partial<{
  of_division?: number;
  code?: string;
  name?: string;
  active_flag?: string;
  limit?: number;
  offset?: number;
}>;

/** =================== Response Types ===================  */

type CommonListResponse = {
  total: number;
  next?: string;
  previous?: string;
};

type EncounterListResponse = {
  items: EncounterSerializer[];
} & CommonListResponse;

type OAUserListResponse = {
  items: ProfiledDivisionHasUserSerializer[];
} & CommonListResponse;

type MiscellaneousListResponse = {
  items: MiscellaneousSerializer[];
} & CommonListResponse;

type ProductListResponse = {
  items: ProductSerializer[];
} & CommonListResponse;

type ProductServiceResponse = {
  items: ProductServiceSerializer[];
} & CommonListResponse;

/** ===================== Constants ====================  */

export const StateInitial: State = {
  SaveServiceOrderSequence: null,
};

export const DataInitial: Data = {};

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

const Masters = [
  ["productType", {}],
  ["unit", {}],
] as const;

/** =================== Helper State =================== */

const setButtonLoadCheck = (
  controller: Controller,
  key: ButtonLoadCheckKey,
  status: BLC_STATUS_TYPE | null
) => {
  controller.setState((prev) => ({
    buttonLoadCheck: {
      ...prev.buttonLoadCheck,
      [key]: status,
    },
  }));
};

const setErrorMessage = (controller: Controller, key: ErrorMessageKey, error: any) => {
  controller.setState((prev) => ({
    errorMessage: {
      ...prev.errorMessage,
      [key]: error,
    },
  }));
};

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.handleEvent({
    message: "GetMasterData",
    params: { masters: Masters },
  });

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.SEARCH]: BLC_STATUS.LOADING,
    },
  });

  // -- init data
  const [
    [oaListResp, oaListErr],
    [productListRes, productListErr],
    [productServiceRes, productServiceErr],
  ] = await Promise.all([
    getOAUserList(controller, { filter: {} }),
    getProductList(controller, {
      filter: {
        limit: DEFAULT_PRODUCT_LIST_LIMIT,
        offset: 0,
      },
    }),
    getProductServiceList(controller, {
      filter: {
        of_division: controller.data.division,
        limit: DEFAULT_PRODUCT_LIST_LIMIT,
        offset: 0,
      },
    }),
  ]);

  let miscellaneousList: any[] = [];
  let doctorFeeOrderList: any[] = [];

  if (state.selectedEncounter?.id) {
    const [[miscellaneousRes, miscellaneousErr], [doctorFeeOrderRes, doctorFeeOrderErr]] =
      await Promise.all([
        MiscellaneousOrderList.list({
          params: { encounter_id: state.selectedEncounter.id, active: true, limit: 99999 },
          apiToken: controller.apiToken,
        }),
        DoctorFeeOrderList.list({
          params: { encounter: state.selectedEncounter.id, limit: 99999 },
          apiToken: controller.apiToken,
        }),
      ]);

    miscellaneousList = miscellaneousRes?.items ?? [];
    doctorFeeOrderList = (doctorFeeOrderRes?.items ?? []).filter(
      (i: { doctor_fee_rule: number }) => i.doctor_fee_rule === DOCTOR_FEE_RULE
    );

    if (!doctorFeeOrderList.length) {
      doctorFeeOrderList = [createDefaultDoctorFee(state.selectedEncounter.id)];
    }
  }

  let encounterList: any[] = [];

  if (state.MeBillingSequence?.selectedCustomer?.patientDetail?.id) {
    const [encounterRes, encounterErr, encounterNet] = await getEncounterList(controller, {
      filter: {
        patient: state.MeBillingSequence?.selectedCustomer?.patientDetail?.id,
        unexpired_only: true,
      },
    });
    encounterList = encounterRes?.items ?? [];
  }

  controller.setState((prev) => ({
    SaveServiceOrderSequence: {
      ...prev.SaveServiceOrderSequence,
      sequenceIndex: "Action",
      serviceOrderPreviewData: miscellaneousList,
      serviceDoctorPreviewData: doctorFeeOrderList,
      OAUserList: oaListResp?.items || [],
      encounterList: encounterList,
      productList: productListRes?.items || [],
      productListPage: {
        ...prev.SaveServiceOrderSequence?.productListPage,
        activePage: 1,
        total: productListRes?.total || 0,
      },
      serviceList: productServiceRes?.items,
      serviceListPage: {
        ...prev.SaveServiceOrderSequence?.serviceListPage,
        activePage: 1,
        total: productServiceRes?.total || 0,
      },
    },
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.SEARCH]: BLC_STATUS.SUCCESS,
    },
  }));

  Action(controller, {
    sequence: "SaveServiceOrder",
    action: "SEARCH_PRODUCT",
  });
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.SEARCH]: HandleSearch,
    [ACTIONS.ADD_SERVICE_ORDER]: HandleAddServiceOrder,
    [ACTIONS.SAVE_SERVICE_ORDER]: HandleSaveServiceOrder,
    [ACTIONS.GET_MISCELLANEOUS_LIST]: HandleMiscellaneousOrderList,
    [ACTIONS.DELETE_SERVICE_ORDER]: HandleDeleteServiceOrder,
    [ACTIONS.DELETE_SERVICE_DOCTOR]: HandleDeleteServiceDoctor,
    [ACTIONS.SELECTED_ENCOUNTER]: HandleSelectedEncounter,
    [ACTIONS.SEARCH_PRODUCT]: HandleSearchProduct,
    [ACTIONS.CREATE_SERVICE]: HandleCreateService,
    [ACTIONS.SEARCH_SERVICE]: HandleSearchService,
  };
  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    handler(controller, params);
  } else {
    console.error(`No handler found for action: ${action}`);
  }
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */
const HandleSearch: Handler<Params<"SEARCH">> = async (controller, params) => {
  const state = controller.getState();

  setButtonLoadCheck(controller, BTN_ACTS.SEARCH, BLC_STATUS.LOADING);

  const activePage = params.activePage || 1;
  const offset = (activePage - 1) * DEFAULT_PRODUCT_LIST_LIMIT;

  const data = {
    of_division: controller.data.division,
    name: params.searchText,
    active_flag: "ACTIVE",
    limit: DEFAULT_PRODUCT_LIST_LIMIT,
    offset,
  };

  const [serviceRes, serviceErr] = await getProductServiceList(controller, {
    filter: data,
  });

  if (serviceRes) {
    setButtonLoadCheck(controller, BTN_ACTS.SEARCH, BLC_STATUS.SUCCESS);

    controller.setState((prev) => ({
      SaveServiceOrderSequence: {
        ...prev.SaveServiceOrderSequence,
        serviceList: serviceRes?.items || [],
        serviceListPage: {
          ...prev.SaveServiceOrderSequence?.serviceListPage,
          activePage: params?.activePage || 0,
          total: serviceRes.total,
        },
      },
    }));
  } else {
    setButtonLoadCheck(controller, BTN_ACTS.SEARCH, BLC_STATUS.ERROR);
    setErrorMessage(controller, MAIN_CARD_NAME, serviceErr);
  }
};

const HandleAddServiceOrder: Handler<Params<"ADD_SERVICE_ORDER">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.ADD_SERVICE_ORDER]: BLC_STATUS.LOADING },
  });

  const data = (params.selectService || []).map((item) => {
    return {
      encounter: state.selectedEncounter?.id,
      quantity: 1,
      eligibility_type: "TREATMENT",
      product: item.id,
    };
  });

  const dfItems = [...(state.SaveServiceOrderSequence?.serviceOrderPreviewData || []), ...data];
  const dfPreviewList = (state.SaveServiceOrderSequence?.serviceOrderPreviewData || []).filter(
    (item: any) => item.id
  );

  const [miscellaneousPreviewRes, miscellaneousPreviewErr] = await MiscellaneousPreviewList.post({
    data: { items: (dfItems ?? []).filter((item) => !item?.id) },
    extra: { division: controller.data.division },
    apiToken: controller.apiToken,
  });

  const doctorFeePreviewItems = (miscellaneousPreviewRes?.items || []).map((item: any) => ({
    ...item,
    encounter: state.selectedEncounter?.id,
  }));

  if (miscellaneousPreviewRes) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.ADD_SERVICE_ORDER]: BLC_STATUS.SUCCESS,
      },
      SaveServiceOrderSequence: {
        ...state.SaveServiceOrderSequence,
        serviceOrderPreviewData: [...dfPreviewList, ...doctorFeePreviewItems],
      },
    });

    params.callback?.(false);
  } else {
    controller.setState({
      buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.ADD_SERVICE_ORDER]: BLC_STATUS.ERROR },
      errorMessage: { ...state.errorMessage, [MAIN_CARD_NAME]: miscellaneousPreviewErr },
    });
  }
};

const HandleSaveServiceOrder: Handler<Params<"SAVE_SERVICE_ORDER">> = async (controller) => {
  const state = controller.getState();
  const seq = state.SaveServiceOrderSequence;
  const encounterId = state.selectedEncounter?.id;

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.SAVE_SERVICE_ORDER]: BLC_STATUS.LOADING,
    },
  });

  const { persisted, draft } = splitByPersisted(seq?.serviceDoctorPreviewData);

  const [previewRes, previewErr] = await DoctorFeeOrderPreviewList.post({
    data: { items: draft?.filter((item) => item.received_employee) },
    extra: { division: controller.data.division },
    apiToken: controller.apiToken,
  });

  if (previewErr) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SAVE_SERVICE_ORDER]: BLC_STATUS.ERROR,
      },
      errorMessage: { ...state.errorMessage, [MAIN_CARD_NAME]: previewErr },
    });
    return;
  }

  const doctorItems = [...persisted, ...(previewRes?.items ?? [])].map((i) => ({
    ...i,
    encounter: encounterId,
  }));

  const [[doctorFeeOrderRes, doctorFeeOrderErr], [miscellaneousRes, miscellaneousErr]] =
    await Promise.all([
      DoctorFeeOrderCreateUpdateList.create({
        data: {
          items: doctorItems,
        } as any,
        extra: { division: controller.data.division },
        apiToken: controller.apiToken,
      }),
      MiscellaneousOrderListCreate.post({
        data: {
          miscellaneous_orders: seq?.serviceOrderPreviewData ?? [],
        } as any,
        extra: { division: controller.data.division },
        apiToken: controller.apiToken,
      }),
    ]);

  if (doctorFeeOrderErr || miscellaneousErr) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SAVE_SERVICE_ORDER]: BLC_STATUS.ERROR,
      },
      errorMessage: {
        ...state.errorMessage,
        [MAIN_CARD_NAME]: [doctorFeeOrderErr, miscellaneousErr],
      },
    });
    return;
  }

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.SAVE_SERVICE_ORDER]: BLC_STATUS.SUCCESS,
    },
  });
};

const HandleMiscellaneousOrderList: Handler<Params<"GET_MISCELLANEOUS_LIST">> = async (
  controller,
  params
) => {
  const state = controller.getState();

  const [miscellaneousOrderListRes, miscellaneousOrderListErr] = await MiscellaneousOrderList.list({
    params: { encounter_id: state.selectedEncounter.id, active: true, limit: 99999 },
    apiToken: controller.apiToken,
  });

  if (miscellaneousOrderListRes) {
    controller.setState({
      SaveServiceOrderSequence: {
        ...state.SaveServiceOrderSequence,
        serviceOrderPreviewData: miscellaneousOrderListRes?.items || [],
      },
    });
  } else {
    controller.setState({
      errorMessage: { ...state.errorMessage, [MAIN_CARD_NAME]: miscellaneousOrderListErr },
    });
  }
};

const HandleDeleteServiceOrder: Handler<Params<"DELETE_SERVICE_ORDER">> = async (
  controller,
  params
) => {
  const state = controller.getState();

  const selectOrder = (state.SaveServiceOrderSequence?.serviceOrderPreviewData || []).find(
    (item, index) => index === params.id
  );

  const newServiceOrderPreview = (
    state.SaveServiceOrderSequence?.serviceOrderPreviewData ?? []
  ).filter((_, i) => i !== params.id);

  if (selectOrder?.id) {
    const [miscellaneousRes, miscellaneousErr] = await MiscellaneousOrderDetail.delete({
      apiToken: controller.apiToken,
      pk: selectOrder?.id,
    });

    if (!miscellaneousErr) {
      controller.setState({
        SaveServiceOrderSequence: {
          ...state.SaveServiceOrderSequence,
          serviceOrderPreviewData: newServiceOrderPreview,
        },
      });

      params.callback?.(null);
    } else {
      controller.setState({
        errorMessage: { ...state.errorMessage, [MAIN_CARD_NAME]: miscellaneousErr },
      });

      params.callback?.(null);
    }
  } else {
    controller.setState({
      SaveServiceOrderSequence: {
        ...state.SaveServiceOrderSequence,
        serviceOrderPreviewData: newServiceOrderPreview,
      },
    });

    params.callback?.(null);
  }
};

const HandleDeleteServiceDoctor: Handler<Params<"DELETE_SERVICE_DOCTOR">> = async (
  controller,
  params
) => {
  const state = controller.getState();

  const newServiceDoctorPreview = (
    state.SaveServiceOrderSequence?.serviceDoctorPreviewData || []
  ).filter((data) =>
    params.data?.tempId ? data?.tempId !== params.data.tempId : data?.id !== params.data?.id
  );

  if (params.data?.id) {
    const [doctorFeeListRes, doctorFeeListErr] = await DoctorFeeOrderDetail.delete({
      apiToken: controller.apiToken,
      pk: params.data?.id,
    });

    if (!doctorFeeListErr) {
      controller.setState({
        SaveServiceOrderSequence: {
          ...state.SaveServiceOrderSequence,
          serviceDoctorPreviewData: newServiceDoctorPreview,
        },
      });
    } else {
      controller.setState({
        errorMessage: { ...state.errorMessage, [MAIN_CARD_NAME]: doctorFeeListErr },
      });
    }
  } else {
    controller.setState({
      SaveServiceOrderSequence: {
        ...state.SaveServiceOrderSequence,
        serviceDoctorPreviewData: newServiceDoctorPreview,
      },
    });
  }
};

const HandleSelectedEncounter: Handler<Params<"SELECTED_ENCOUNTER">> = async (
  controller,
  params
) => {
  const state = controller.getState();

  controller.setState({
    loadingStatus: {
      ...state.loadingStatus,
      [MAIN_CARD_NAME]: true,
    },
  });

  const encounter = params.encounterData;

  if (!encounter?.id) {
    controller.setState({
      selectedEncounter: {},
      loadingStatus: {
        ...state.loadingStatus,
        [MAIN_CARD_NAME]: false,
      },
      SaveServiceOrderSequence: {
        ...state.SaveServiceOrderSequence,
        serviceOrderPreviewData: [],
        serviceDoctorPreviewData: [],
      },
    });
    return;
  }

  const [[miscellaneousRes, miscellaneousErr], [doctorFeeOrderRes, doctorFeeOrderErr]] =
    await Promise.all([
      MiscellaneousOrderList.list({
        params: { encounter_id: encounter.id, active: true, limit: 99999 },
        apiToken: controller.apiToken,
      }),
      DoctorFeeOrderList.list({
        params: { encounter: encounter.id, limit: 99999 },
        apiToken: controller.apiToken,
      }),
    ]);

  let serviceDoctor = (doctorFeeOrderRes?.items ?? []).filter(
    (i: { doctor_fee_rule: number }) => i.doctor_fee_rule === DOCTOR_FEE_RULE
  );

  if (!serviceDoctor.length) {
    serviceDoctor = [createDefaultDoctorFee(encounter.id)];
  }

  controller.setState({
    selectedEncounter: encounter,
    loadingStatus: {
      ...state.loadingStatus,
      [MAIN_CARD_NAME]: false,
    },
    SaveServiceOrderSequence: {
      ...state.SaveServiceOrderSequence,
      serviceOrderPreviewData: miscellaneousRes?.items ?? [],
      serviceDoctorPreviewData: serviceDoctor,
    },
  });
};

const DOCTOR_FEE_RULE = 305352;

const splitByPersisted = (list: any[] = []) => ({
  persisted: list.filter((i) => i?.id),
  draft: list.filter((i) => !i?.id),
});

const createDefaultDoctorFee = (encounterId?: number) => ({
  tempId: crypto.randomUUID(),
  encounter: encounterId,
  doctor_fee_rule: DOCTOR_FEE_RULE,
  doctor_fee_rule_display: "ค่าธรรมเนียมผู้ให้บริการ",
  service_code: "18010015",
  quantity: 1,
  price_unit: 0,
  compensation_price: 0,
});

const HandleSearchProduct: Handler<Params<"SEARCH_PRODUCT">> = async (controller, params) => {
  setButtonLoadCheck(controller, BTN_ACTS.SEARCH_PRODUCT, BLC_STATUS.LOADING);

  const activePage = params.activePage || 1;
  const offset = (activePage - 1) * DEFAULT_PRODUCT_LIST_LIMIT;

  const data: ProductListParams = {
    ...params.filter,
    offset,
    limit: DEFAULT_PRODUCT_LIST_LIMIT,
  };

  const [productListRes, productListErr, productListNet] = await ProductList.list({
    params: data,
    extra: { division: controller.data.division },
    apiToken: controller.apiToken,
  });

  if (productListRes) {
    setButtonLoadCheck(controller, BTN_ACTS.SEARCH_PRODUCT, BLC_STATUS.SUCCESS);

    controller.setState((prev) => ({
      SaveServiceOrderSequence: {
        ...prev.SaveServiceOrderSequence,
        productList: productListRes?.items || [],
        productListPage: {
          ...prev.SaveServiceOrderSequence?.productListPage,
          activePage: params?.activePage || 0,
          total: productListRes.total,
        },
      },
    }));
  } else {
    setButtonLoadCheck(controller, BTN_ACTS.SEARCH, BLC_STATUS.ERROR);
    setErrorMessage(controller, MAIN_CARD_NAME, productListErr);
  }
};

const HandleCreateService: Handler<Params<"CREATE_SERVICE">> = async (controller, params) => {
  setButtonLoadCheck(controller, BTN_ACTS.CREATE_SERVICE, BLC_STATUS.LOADING);

  const DATE_FORMAT = "YYYY-MM-DD";

  const data = {
    ...params.data,
    ...(params.data?.start_date && {
      start_date: (parseDate(params.data?.start_date, true) as moment.Moment).format(DATE_FORMAT),
    }),
    ...(params.data?.end_date && {
      end_date: (parseDate(params.data?.end_date, true) as moment.Moment).format(DATE_FORMAT),
    }),
    code: `${params.data?.of_division}_${params.data?.code}`,
  };

  let response;

  if (params.isEdit) {
    response = await ProductServiceRetrieveUpdateAPIView.update({
      pk: params.data?.id,
      data,
      extra: { division: controller.data.division },
      apiToken: controller.apiToken,
    });
  } else {
    response = await ProductServiceListCreateAPIView.create({
      data,
      extra: { division: controller.data.division },
      apiToken: controller.apiToken,
    });
  }

  const [productServiceRes, productServiceErr, productServiceNet] = response;

  if (productServiceRes) {
    setButtonLoadCheck(controller, BTN_ACTS.CREATE_SERVICE, BLC_STATUS.SUCCESS);

    HandleSearchService(controller, {
      sequence: "SaveServiceOrder",
      action: "SEARCH_SERVICE",
      filter: {
        of_division: controller.data.division,
      },
    });

    params.callback?.(false);
  } else {
    setButtonLoadCheck(controller, BTN_ACTS.CREATE_SERVICE, BLC_STATUS.ERROR);
    setErrorMessage(controller, MAIN_CARD_NAME, productServiceErr);
  }
};

const HandleSearchService: Handler<Params<"SEARCH_SERVICE">> = async (controller, params) => {
  const state = controller.getState();

  setButtonLoadCheck(controller, BTN_ACTS.SEARCH_SERVICE, BLC_STATUS.LOADING);

  const activePage = params.activePage || 1;
  const offset = (activePage - 1) * DEFAULT_PRODUCT_LIST_LIMIT;

  const data = {
    ...params.filter,
    limit: DEFAULT_PRODUCT_LIST_LIMIT,
    offset,
  };

  const [productServiceRes, productServiceErr] = await getProductServiceList(controller, {
    filter: data,
  });

  if (productServiceRes) {
    controller.setState((prev) => ({
      SaveServiceOrderSequence: {
        ...prev.SaveServiceOrderSequence,
        serviceList: productServiceRes?.items,
        serviceListPage: {
          ...prev.SaveServiceOrderSequence?.serviceListPage,
          activePage: params?.activePage || 0,
          total: productServiceRes.total,
        },
      },
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SEARCH_SERVICE]: BLC_STATUS.SUCCESS,
      },
    }));
  } else {
    setButtonLoadCheck(controller, BTN_ACTS.SEARCH_SERVICE, BLC_STATUS.ERROR);
    setErrorMessage(controller, MAIN_CARD_NAME, productServiceErr);
  }
};

/** ====================================================  */
/**                         APIs                          */
/** ====================================================  */

const getOAUserList: Handler<
  { filter: any }, // ***
  Promise<[OAUserListResponse | null, any]>
> = async (controller, params) => {
  const [resp, err] = await OfficialAccountUserList.list({
    apiToken: controller.apiToken,
    params: params.filter,
    extra: { division: controller.data.division },
  });

  if (err || !resp) return [null, err];

  return [resp as OAUserListResponse, null];
};

const getProductList: Handler<
  { filter: ProductListParams },
  Promise<[ProductListResponse | null, any]>
> = async (controller, params) => {
  const [resp, err] = await ProductList.list({
    apiToken: controller.apiToken,
    params: params.filter,
    extra: { division: controller.data.division },
  });

  if (err || !resp) return [null, err];

  return [resp as ProductListResponse, null];
};

const getProductServiceList: Handler<
  { filter: ProductServiceParams },
  Promise<[ProductServiceResponse | null, any]>
> = async (controller, params) => {
  const { active_flag, ...restFilter } = params.filter || {};

  const [resp, err] = await ProductServiceListCreateAPIView.list({
    apiToken: controller.apiToken,
    params: {
      ...restFilter,
      ...(active_flag !== "ALL" && { active_flag }),
      of_division: controller.data.division,
    },
    extra: { division: controller.data.division },
  });

  if (err || !resp) return [null, err];

  return [resp as ProductServiceResponse, null];
};

const getEncounterList: Handler<
  { filter: any },
  Promise<[EncounterListResponse | null, any, any]>
> = async (controller, params) => {
  const [resp, err, net] = await EncounterList.list({
    apiToken: controller.apiToken,
    params: params.filter,
    extra: { division: controller.data.division },
  });

  if (err || !resp) return [null, err, net];

  return [resp as EncounterListResponse, null, net];
};

export const serviceStatusOption = [
  {
    key: "ACTIVE",
    value: "ACTIVE",
    text: "ขายได้ปกติ",
  },
  {
    key: "INACTIVE",
    value: "INACTIVE",
    text: "ยังไม่พร้อมขาย",
  },
];
